import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PiDotsSixVertical } from 'react-icons/pi'
import Checkbox from 'components/Checkbox/checkbox'
import { REACT_SELECT_DEFAULT_CONTROL_HEIGHT } from 'components/FormUtils/ReactSelectWrapper'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'

export type ColumnOption = { value: string; label: string }

/** react-select's caret (separator + chevron), matching the other dropdowns. */
function SelectCaret() {
  return (
    <span className="flex items-center self-stretch text-fg-muted">
      <span className="my-2 w-px self-stretch bg-fg-muted" aria-hidden="true" />
      <span className="flex px-2">
        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502-0.408-0.418-0.436-1.17 0-1.615z"
          />
        </svg>
      </span>
    </span>
  )
}

/** A draggable, checkable column row in the picker's selected list. */
function SortableColumnRow({
  id,
  label,
  onToggle,
}: {
  id: string
  label: string
  onToggle: () => void
}) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  const rowId = `colpick-${id}`
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded px-1 py-1.5 hover:bg-surface-hover',
        isDragging && 'bg-surface-hover'
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className="flex cursor-grab touch-none items-center text-fg-muted hover:text-fg active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <PiDotsSixVertical size={16} />
      </button>
      <Checkbox id={rowId} boxSize="sm" checked onChange={onToggle} />
      <label
        htmlFor={rowId}
        className="flex-1 cursor-pointer select-none text-sm text-fg"
      >
        {label}
      </label>
    </div>
  )
}

/**
 * Columns dropdown with both selection (checkboxes) and ordering (drag handles),
 * like the deal sales-stage organize modal. Selected columns sort to the top as
 * a sortable list (drag to reorder); unselected columns sit below to add. The
 * selection array IS the column order — no canonical re-sort.
 */
export function ColumnPicker({
  label,
  allFields,
  selected,
  onChange,
  idPrefix,
  className,
}: {
  label: string
  allFields: ColumnOption[]
  /** Selected columns, in display/output order. */
  selected: string[]
  onChange: (next: string[]) => void
  idPrefix: string
  className?: string
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const labelOf = (value: string) =>
    allFields.find((f) => f.value === value)?.label ?? value
  const selectedSet = new Set(selected)
  const unselected = allFields.filter((f) => !selectedSet.has(f.value))

  const summary =
    selected.length === 0
      ? 'Choose columns'
      : selected.length === allFields.length
        ? 'All columns'
        : `${selected.length} column${selected.length === 1 ? '' : 's'}`

  const remove = (value: string) =>
    onChange(selected.filter((v) => v !== value))
  const add = (value: string) => onChange([...selected, value])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = selected.indexOf(String(active.id))
    const newIndex = selected.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(selected, oldIndex, newIndex))
  }

  return (
    <div className={cn('min-w-44', className)}>
      <div className="text-xs text-fg-secondary mb-1">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            style={{ minHeight: REACT_SELECT_DEFAULT_CONTROL_HEIGHT }}
            className="form-control-select flex w-full items-center bg-surface focus:outline-none"
          >
            <span
              className={cn(
                'flex-1 truncate px-2 text-left text-sm',
                selected.length === 0
                  ? 'form-control-select-placeholder'
                  : 'text-fg'
              )}
            >
              {summary}
            </span>
            <SelectCaret />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-80 w-64 overflow-y-auto p-2"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selected}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col">
                {selected.map((value) => (
                  <SortableColumnRow
                    key={value}
                    id={value}
                    label={labelOf(value)}
                    onToggle={() => remove(value)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {unselected.length > 0 && selected.length > 0 && (
            <div className="my-1 border-t border-line" />
          )}
          <div className="flex flex-col">
            {unselected.map((f) => {
              const id = `colpick-add-${idPrefix}-${f.value}`
              return (
                <div
                  key={f.value}
                  // Align with sortable rows above (grip width) so checkboxes line up.
                  className="flex items-center gap-2 rounded py-1.5 pl-[26px] pr-1 hover:bg-surface-hover"
                >
                  <Checkbox
                    id={id}
                    boxSize="sm"
                    checked={false}
                    onChange={() => add(f.value)}
                  />
                  <label
                    htmlFor={id}
                    className="flex-1 cursor-pointer select-none text-sm text-fg-secondary"
                  >
                    {f.label}
                  </label>
                </div>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
